<template>
  <div class="inline-flex gap-2">
    <div class="inline-flex rounded-md border border-neutral-450 border-solid overflow-hidden leading-7">
      <select class="block border-0 py-1 px-3 ring-inset" v-model.number="selectedDay">
        <option v-for="{ value, label } in days" :key="value" :value="value">
          {{ label }}
        </option>
      </select>
      <select class="block border-y-0 border-neutral-450 py-1 px-3 ring-inset" v-model.number="selectedMonth">
        <option v-for="{ value, label } in months" :key="value" :value="value">
          {{ label }}
        </option>
      </select>
      <select class="block border-0 border-neutral-450 py-1 px-3 ring-inset" v-model.number="selectedYear">
        <option v-for="{ value, label } in years" :key="value" :value="value">{{ label }}</option>
      </select>
    </div>
    <div v-if="mode === 'dateTime'"
      class="inline-flex rounded-md border border-neutral-450 border-solid divide-x overflow-hidden leading-7">
      <select class="block border-0 py-1 px-3 ring-inset" v-model.number="selectedHour">
        <option v-for="{ value, label } in hours" :key="value" :value="value">
          {{ label }}
        </option>
      </select>
      <select class="block border-0 border-neutral-450 py-1 px-3 ring-inset" v-model.number="selectedMinute">
        <option v-for="{ value, label } in minutes" :key="value" :value="value">
          {{ label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script>
import {
  timeDays,
  timeMonth,
  timeMonths,
  timeYear,
  timeYears,
} from 'd3-time';
import { range } from 'd3-array';

export default {
  props: {
    locale: {
      type: String,
      default: 'en',
      validator: (l) => ['en', 'de'].includes(l),
    },
    mode: {
      type: String,
      default: 'date',
      validator: (m) => ['date', 'dateTime'].includes(m),
    },
    modelValue: {
      default: new Date(),
    },
  },
  computed: {
    date() {
      return this.modelValue ?? new Date();
    },
    days() {
      const dayFormat = new Intl.DateTimeFormat(this.locale, { day: '2-digit' });
      const start = timeMonth.floor(this.date);
      const stop = timeMonth.offset(start, 1);
      const days = timeDays(start, stop);
      return days.map((d) => ({
        value: d.getDate(),
        label: dayFormat.format(d),
      }));
    },
    selectedDay: {
      get() {
        return this.date.getDate();
      },
      set(day) {
        // note: do not mutate the original date object (!)
        const newDate = new Date(this.date);
        newDate.setDate(day);
        this.$emit('update:modelValue', newDate);
      },
    },
    months() {
      const monthFormat = new Intl.DateTimeFormat(this.locale, { month: 'long' });
      const start = timeYear.floor(this.date);
      const end = timeYear.offset(start, 1);
      const months = timeMonths(start, end);
      return months.map((d) => ({
        value: d.getMonth(),
        label: monthFormat.format(d),
      }));
    },
    selectedMonth: {
      get() {
        return this.date.getMonth();
      },
      set(month) {
        // note: do not mutate the original date object (!)
        const newDate = new Date(this.date);
        newDate.setMonth(month);
        this.$emit('update:modelValue', newDate);
      },
    },
    years() {
      const now = new Date();
      const years = timeYears(timeYear.offset(now, -15), timeYear.offset(now, 15));
      return years.map((d) => ({
        value: d.getFullYear(),
        label: d.getFullYear(),
      }));
    },
    selectedYear: {
      get() {
        return this.date.getFullYear();
      },
      set(year) {
        // note: do not mutate the original date object (!)
        const newDate = new Date(this.date);
        newDate.setFullYear(year);
        this.$emit('update:modelValue', newDate);
      },
    },
    hours() {
      return range(0, 24).map((n) => ({
        value: n,
        label: n.toString().padStart(2, '0'),
      }));
    },
    selectedHour: {
      get() {
        return this.date.getHours();
      },
      set(hours) {
        // note: do not mutate the original date object (!)
        const newDate = new Date(this.date);
        newDate.setHours(hours);
        this.$emit('update:modelValue', newDate);
      },
    },
    minutes() {
      return range(0, 60).map((n) => ({
        value: n,
        label: n.toString().padStart(2, '0'),
      }));
    },
    selectedMinute: {
      get() {
        return this.date.getMinutes();
      },
      set(minutes) {
        // note: do not mutate the original date object (!)
        const newDate = new Date(this.date);
        newDate.setMinutes(minutes);
        this.$emit('update:modelValue', newDate);
      },
    },
  },
};
</script>

<style scoped>
select {
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCA4IDUiPgogIDxwYXRoIGZpbGw9IiMxQzM4NUYiIGQ9Ik0uNSAwaDdjLjQgMCAuNy42LjMgMUw0LjQgNC44Yy0uMi4zLS42LjMtLjggMEwuMiAxQy0uMi42IDAgMCAuNSAwWiIvPgo8L3N2Zz4K");
  background-position: right 0.3em center;
  background-repeat: no-repeat;
  background-size: 0.5em 0.5em;
  padding-right: 2rem;
  appearance: none;
}
</style>
